import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { DeleteButton, List, useDataGrid } from "@refinedev/mui";

// One row of GET /api/v2/admin/story-comments. Roots and replies arrive flat —
// a reply is a row whose parentId points at its root. `authorName` / `storyTitle`
// are null when the user or story behind the comment is gone.
type StoryCommentRecord = {
  id: number;
  // The story's hex shelf id (e.g. "0925d52c"); null when it no longer names one.
  storyId: string | null;
  storyTitle: string | null;
  userId: number;
  authorName: string | null;
  authorEmail: string | null;
  comment: string;
  mediaUrl: string | null;
  parentId: number | null;
  likes: number;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const StoryCommentList = () => {
  // Server-ordered: newest first. Columns are not sortable for the same reason
  // as Story Views — the shared data provider sends page/limit only, so
  // client-side sorting would reorder just the current page.
  const { dataGridProps } = useDataGrid({});

  const columns = React.useMemo<GridColDef<StoryCommentRecord>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
        sortable: false,
        display: "flex",
        // The Reports queue names a comment as "comment #N", so without this
        // column there was no way to get from a report to the row it is about.
        // Rendered the same way a reply's `↳ #N` chip is, so the two read as
        // the same kind of identifier.
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            #{row.id}
          </Typography>
        ),
      },
      {
        field: "storyId",
        headerName: "Story",
        flex: 0.9,
        minWidth: 180,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.storyTitle ?? (
                <span style={{ opacity: 0.6 }}>(story deleted)</span>
              )}
            </Typography>
            {row.storyId ? (
              <Typography variant="caption" color="text.secondary">
                <code>{row.storyId}</code>
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        field: "authorName",
        headerName: "Author",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.authorName ?? <span style={{ opacity: 0.6 }}>(unknown)</span>}
            </Typography>
            <Tooltip title={row.authorEmail ?? ""}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.authorEmail ?? `user ${row.userId}`}
              </Typography>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "comment",
        headerName: "Comment",
        flex: 2,
        minWidth: 280,
        sortable: false,
        display: "flex",
        // Replies are flagged rather than indented — the grid is flat, and the
        // chip is what tells you this row hangs under another one.
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, width: "100%" }}
          >
            {row.parentId != null ? (
              <Chip size="small" variant="outlined" label={`↳ #${row.parentId}`} />
            ) : null}
            <Tooltip title={row.comment}>
              <Typography variant="body2" noWrap>
                {row.comment}
              </Typography>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "mediaUrl",
        headerName: "Still",
        minWidth: 80,
        sortable: false,
        display: "flex",
        // A scene still the commenter attached from their own playthrough.
        renderCell: ({ row }) =>
          row.mediaUrl ? (
            <Link href={row.mediaUrl} target="_blank" rel="noreferrer">
              <Avatar
                variant="rounded"
                src={row.mediaUrl}
                alt="attached still"
                sx={{ width: 40, height: 40 }}
              />
            </Link>
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        field: "likes",
        headerName: "Likes",
        minWidth: 90,
        align: "right",
        headerAlign: "right",
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FavoriteIcon
              fontSize="inherit"
              color={row.likes > 0 ? "error" : "disabled"}
            />
            <span>{row.likes}</span>
          </Stack>
        ),
      },
      {
        field: "replyCount",
        headerName: "Replies",
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <span>{row.replyCount}</span>,
      },
      {
        field: "createdAt",
        headerName: "Posted",
        minWidth: 180,
        sortable: false,
        display: "flex",
        valueFormatter: (value) =>
          formatDateTime(value as string | null | undefined),
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 90,
        sortable: false,
        display: "flex",
        // Delete only — moderation removes a comment, it doesn't rewrite it.
        // Deleting a root cascades to its replies, so say so in the confirm.
        renderCell: ({ row }) => (
          <DeleteButton
            hideText
            recordItemId={row.id}
            confirmTitle={
              row.replyCount > 0
                ? `Delete this comment and its ${row.replyCount} ${
                    row.replyCount === 1 ? "reply" : "replies"
                  }?`
                : "Delete this comment?"
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Every comment across every story, newest first. Replies are listed
          alongside their roots — deleting a root also deletes its replies.
        </Typography>
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        disableColumnFilter
        rowHeight={64}
      />
    </List>
  );
};
